import moment from "moment";
import Close from "@mui/icons-material/Close";
import Search from "@mui/icons-material/Search";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AutocompleteAPI, DataTable, DatePicker, FormField, Typography, useCommonComponentContext } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Screens from "constants/screens";
import Strings from "constants/strings";
import Constants from "constants/index";
import useDataCommon from "hooks/useDataCommon.hook";
import SearchService from "services/search/search.service";
import StatisticsService from "services/medkit/statistics.service";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { IRecordSynthetic } from "commons/interfaces";
import { setListPathName } from "store/slice/titleRoute.slice";
import { setDataAlert, showLoadingDataTable } from "store/slice/common.slice";



export interface IDataFilter {
    date?: any;
    cityId?: string;
    projectId?: string;
    organizationName?: string;

    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
};

const searchService = new SearchService();
const statisticsService = new StatisticsService();

const SyntheticScreen = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const { serviceCode, tenantCode } = useCommonComponentContext();
    const { getDataProjects, getSearchQueryByGeography } = useDataCommon();

    const loadingDataTable = useSelector((state: RootState) => state.common.loadingDataTable);

    const [dataFilter, setDataFilter] = useState<IDataFilter>({});
    const [dataRows, setDataRows] = useState<{ id: string, name: string }[]>([]);
    const [dataMapSynthetic, setDataMapSynthetic] = useState<Map<string, IRecordSynthetic>>(new Map());

    const pageSize = searchParams.get("size");
    const pageNumber = searchParams.get("page");
    const paramsDate = searchParams.get("date");
    const paramsCityId = searchParams.get("city");
    const paramsSearchText = searchParams.get("text");
    const paramsProjectId = searchParams.get("project");

    useEffect(() => {
        const size = Number(pageSize || Constants.ROW_PER_PAGE);
        const page = Number(pageNumber || Constants.PAGE_DEFAULT);

        let newDate: any = moment().unix();
        if (!Helpers.isNullOrEmpty(paramsDate) && (paramsDate !== "0")) {
            newDate = paramsDate;
        };

        const newReq: IDataFilter = {
            date: newDate,
            pageSize: size,
            pageNumber: page,
            cityId: paramsCityId,
            projectId: paramsProjectId,
            organizationName: paramsSearchText,
        };

        loadDataSynthetic(newReq);
    }, [
        pageSize,
        pageNumber,
        paramsDate,
        paramsCityId,
        paramsProjectId,
        paramsSearchText,
    ]);

    const loadDataSynthetic = async (newReq: IDataFilter) => {
        try {
            dispatch(showLoadingDataTable(true));

            let orgIds: string[] = [];
            let rows: { id: string, name: string }[] = [];
            const newDataMap: Map<string, IRecordSynthetic> = new Map();

            if (newReq?.cityId || newReq?.organizationName) {
                const resultSearchOrgganization = await searchService.queryOrganization({
                    organization: {
                        province: newReq?.cityId || undefined,
                        name: newReq?.organizationName || undefined,
                        tenantCode: tenantCode,
                        serviceCode: serviceCode,
                    },
                    pageSize: newReq.pageSize,
                    pageNumber: newReq.pageNumber,
                });

                for (const element of [...resultSearchOrgganization?.items || []]) {
                    const valueName = Helpers.getDefaultValueMultiLanguage(element.name, "vi", true);

                    orgIds.push(element.id);
                    rows.push({ id: element.id, name: valueName });
                };

                if (orgIds.length > 0) {
                    const projectIds = newReq.projectId ? [newReq.projectId] : [];

                    const result = await statisticsService.getSyntheticList({
                        date: newReq.date,
                        projectIds: projectIds,
                        organizationIds: orgIds,
                    });

                    for (const element of [...result || []]) {
                        newDataMap.set(element.organizationId, element);
                    };
                };

                setDataRows(rows);
                setDataMapSynthetic(newDataMap);
                setDataFilter({
                    date: newReq?.date,
                    cityId: newReq?.cityId,
                    projectId: newReq?.projectId,
                    organizationName: newReq?.organizationName,
                    pageSize: resultSearchOrgganization?.pageSize || 10,
                    pageNumber: resultSearchOrgganization?.pageNumber || 1,
                    totalCount: resultSearchOrgganization?.totalCount || 0,
                });
            } else {
                setDataRows(rows);
                setDataMapSynthetic(newDataMap);
                setDataFilter({
                    date: newReq?.date,
                    cityId: newReq?.cityId,
                    projectId: newReq?.projectId,
                    organizationName: newReq?.organizationName,
                    pageSize: 10,
                    pageNumber: 1,
                    totalCount: 0,
                });
            }
        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoadingDataTable(false));
        }
    };

    const handleFilterData = (req: IDataFilter) => {
        let query: string = `?page=${req.pageNumber || Constants.PAGE_DEFAULT}&size=${req.pageSize || Constants.ROW_PER_PAGE}`;

        if (!Helpers.isNullOrEmpty(req.date) && req.date !== "0" && req.date !== 0) {
            query += `&date=${req.date}`;
        } else {
            query += `&date=${moment().unix()}`;
        }

        if (!Helpers.isNullOrEmpty(req.projectId)) {
            query += `&project=${req.projectId}`;
        }

        if (!Helpers.isNullOrEmpty(req.cityId)) {
            query += `&city=${req.cityId}`;
        }

        if (!Helpers.isNullOrEmpty(req.organizationName)) {
            query += `&text=${req.organizationName}`;
        }

        navigate(Screens.SYNTHETIC + query, { replace: true });

        dispatch(setListPathName({ pathname: Screens.SYNTHETIC, query }));
    };

    const dataColumns = useMemo(() => {
        return ([
            {
                Header: Strings.SYNTHETIC.ORGANIZATION_NAME, accessor: "name", width: "200px",
                Cell: (row: any) => (
                    <>
                        <Tooltip title={row.value}>
                            <Typography variant="caption" color="secondary" fontWeight="bold">{row.value}</Typography>
                        </Tooltip>
                    </>
                ),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 1`, accessor: "jan", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.jan || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 2`, accessor: "feb", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.feb || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 3`, accessor: "mar", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.mar || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 4`, accessor: "apr", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.apr || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 5`, accessor: "may", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.may || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 6`, accessor: "jun", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.jun || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 7`, accessor: "jul", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.jul || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 8`, accessor: "aug", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.aug || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 9`, accessor: "sep", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.sep || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 10`, accessor: "oct", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.oct || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 11`, accessor: "nov", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.nov || "0"}</>),
            },
            {
                Header: `${Strings.DATE_RANGE_PICKER.MONTH} 12`, accessor: "december", width: "60px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.december || "0"}</>),
            },

            {
                Header: Strings.SYNTHETIC.DURING_WEEK, accessor: "thisWeek", width: "90px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.thisWeek || "0"}</>),
            },
            {
                Header: Strings.SYNTHETIC.DURING_YEAR, accessor: "thisYear", width: "90px",
                Cell: (row: any) => (<>{dataMapSynthetic.get(row.row?.original?.id)?.thisYear || "0"}</>),
            },
            {
                Header: Strings.SYNTHETIC.FREQUENT_USERS, accessor: "countActiveUser", width: "100px",
                Cell: (row: any) => (<>{"0"}</>),
            },
        ]);
    }, [dataMapSynthetic]);

    return (
        <DashboardLayout
            isPermission
            requiredOrganization
            title={Strings.SYNTHETIC.TITLE}
            route={[{ title: Strings.SYNTHETIC.TITLE, route: "" }]}
        >
            <Card sx={{ padding: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} >
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormField
                                    fullWidth
                                    size={"small"}
                                    maxLength={255}
                                    variant={"outlined"}
                                    label={Strings.SYNTHETIC.ORGANIZATION_NAME}
                                    defaultValue={dataFilter?.organizationName || ""}
                                    placeholder={Strings.SYNTHETIC.PLACEHOLDER_SEARCH_TEXT}
                                    onBlur={(e: any) => {
                                        handleFilterData({
                                            ...dataFilter,
                                            organizationName: e.target.value,
                                        });
                                    }}
                                    onKeyPress={(e: any) => {
                                        if (e.key === "Enter") {
                                            handleFilterData({
                                                ...dataFilter,
                                                organizationName: e.target.value,
                                            });
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <IconButton
                                                color="info"
                                                onClick={() => {
                                                    handleFilterData({
                                                        ...dataFilter,
                                                        organizationName: dataFilter?.organizationName,
                                                    });
                                                }}
                                            >
                                                <Search />
                                            </IconButton>
                                        ),
                                        endAdornment: !Helpers.isNullOrEmpty(dataFilter?.organizationName) && (
                                            <IconButton
                                                color="secondary"
                                                onClick={() => {
                                                    handleFilterData({
                                                        ...dataFilter,
                                                        organizationName: undefined,
                                                    });
                                                }}
                                            >
                                                <Close />
                                            </IconButton>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DatePicker
                                    views={["year"]}
                                    variant={"outlined"}
                                    label={Strings.COMMON.DATETIME}
                                    value={Helpers.toUnix(dataFilter?.date)}
                                    onChangeValue={(value) => {
                                        const newValue = value ? moment(value).unix() : undefined;
                                        handleFilterData({
                                            ...dataFilter,
                                            date: newValue,
                                        });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <AutocompleteAPI
                                    variant={"outlined"}
                                    defaultValue={dataFilter?.projectId}
                                    label={Strings.PROJECT_MANAGEMENT.PROJECT}
                                    placeholder={Strings.PROJECT_MANAGEMENT.SELECT_PROJECT}
                                    getApi={getDataProjects}
                                    requestGetApi={{}}
                                    onChange={(value) => { }}
                                    onChangeItem={(value) => {
                                        handleFilterData({
                                            ...dataFilter,
                                            projectId: value?.code as string,
                                        });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <AutocompleteAPI
                                    variant={"outlined"}
                                    label={Strings.SYNTHETIC.CITY}
                                    defaultValue={dataFilter?.cityId}
                                    placeholder={Strings.SYNTHETIC.CITY}
                                    getApi={getSearchQueryByGeography}
                                    requestGetApi={{}}
                                    onChange={(value) => { }}
                                    onChangeItem={(value) => {
                                        handleFilterData({
                                            ...dataFilter,
                                            cityId: value?.code as string,
                                        });
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <DataTable
                            isShowIndex
                            hideActionMenu
                            loading={loadingDataTable}
                            totalCount={dataFilter?.totalCount}
                            table={{ columns: dataColumns as any, rows: dataRows }}
                            rowPerPage={dataFilter?.pageSize || Constants.ROW_PER_PAGE}
                            pageNumber={dataFilter?.pageNumber || Constants.PAGE_DEFAULT}
                            onChangePageSize={(value) => {
                                handleFilterData({ ...dataFilter, pageSize: value, });
                            }}
                            onChangePageNumber={(value) => {
                                handleFilterData({ ...dataFilter, pageNumber: value, });
                            }}
                        />
                    </Grid>
                </Grid>
            </Card>
        </DashboardLayout>
    );
};

export default SyntheticScreen;